import { ReactNode } from 'react';

import type { FileRejection } from 'react-dropzone';
import toast from 'react-hot-toast';

import {
  FileUploadsDropWrapper,
  getUnsupportedFilesMessage,
} from '@liscio/common';
import {
  Card,
  CardContent,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
  styled,
  Button,
} from '@liscio/ui';

import { ActiveDragOverlayBlue } from '../ActiveDragOverlayBlue/ActiveDragOverlayBlue';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  flex: 1,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[1],
    transform: 'translateY(-1px)',
  },
}));

const StyledCardContent = styled(CardContent)(() => ({
  padding: 24, // theme.spacing(3)
  height: '100%',
}));

const IconBox = styled(Stack)(() => ({
  width: 48,
  height: 48,
  borderRadius: 8, // theme.spacing(1)
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledUploadCard = styled(Card)(({ theme }) => ({
  width: '100%',
  cursor: 'pointer',
  border: `2px dashed ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.primary.light + '10',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.primary.light + '20',
    borderColor: theme.palette.primary.dark,
  },
}));

const UploadIconBox = styled(Stack)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light + '30',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledListItem = styled(ListItem)(() => ({
  paddingLeft: 0,
  paddingRight: 0,
}));

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 32,
}));

export interface UploadCardProps {
  title: string;
  subtitle: string;
  description: string;
  features: Array<{
    title: string;
    description: string;
  }>;
  icon: 'folderOpen' | 'send';
  iconColor: 'primary' | 'secondary';
  onDrop: (files: File[]) => void;
  uploadButtonText: string;
  uploadDescription: string;
  instructionText: string;
  technicalSpecs: ReactNode;
  disabled?: boolean;
  multiple?: boolean;
  maxFiles?: number;
  /** Hides the description and technical specs and tightens the feature list */
  compact?: boolean;
}

const TOO_MANY_FILES_CODE = 'too-many-files';

// Our handler replaces the hook's default toast, so it must cover both cases:
// the too-many-files message depends on the `maxFiles` prop.
const createRejectedFilesHandler =
  (maxFiles?: number) => (fileRejections: FileRejection[]) => {
    const tooManyFiles = fileRejections.some(({ errors }) =>
      errors.some(({ code }) => code === TOO_MANY_FILES_CODE)
    );
    if (!tooManyFiles) {
      toast.error(getUnsupportedFilesMessage(fileRejections));
      return;
    }
    toast.error(
      maxFiles && maxFiles > 1
        ? `Upload up to ${maxFiles} files at a time.`
        : 'Upload one file at a time.'
    );
  };

export const UploadCard = ({
  title,
  subtitle,
  description,
  features,
  icon,
  iconColor,
  onDrop,
  uploadButtonText,
  uploadDescription,
  instructionText,
  technicalSpecs,
  disabled = false,
  multiple = true,
  maxFiles,
  compact = false,
}: UploadCardProps) => {
  const theme = useTheme();

  return (
    <StyledCard
      data-testid="upload-card"
      variant="outlined"
      sx={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
    >
      <StyledCardContent>
        <Stack spacing={3} height="100%">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconBox
              sx={{
                bgcolor: theme.palette[iconColor].light + '20',
              }}
            >
              <Icon icon={icon} color={iconColor} fontSize="large" />
            </IconBox>
            <Stack>
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {subtitle}
              </Typography>
            </Stack>
          </Stack>

          {!compact && (
            <Typography variant="body1" color="text.primary">
              {description}
            </Typography>
          )}

          <Stack spacing={2} flex={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              Key Features:
            </Typography>
            <List dense>
              {features.map((feature, index) => (
                <StyledListItem
                  key={index}
                  sx={compact ? { py: 0 } : undefined}
                >
                  <StyledListItemIcon>
                    <Icon icon="check" color="success" fontSize="small" />
                  </StyledListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                    <b>{feature.title}:</b> {feature.description}
                  </ListItemText>
                </StyledListItem>
              ))}
            </List>
          </Stack>

          <Stack spacing={3} alignItems="center">
            <FileUploadsDropWrapper 
              sx={{ width: '100%', minWidth: 0 }}
              data-testid="file-drop-zone"
              activeDragComponent={<ActiveDragOverlayBlue />}
              options={{
                onDropAccepted: onDrop,
                onDropRejected: createRejectedFilesHandler(maxFiles),
                accept: { 'application/pdf': ['.pdf'] },
                multiple,
                maxFiles,
                noClick: false, // Enable click to browse
              }}
            >
              <StyledUploadCard variant="outlined">
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Stack spacing={3} alignItems="center">
                    <Stack spacing={3} alignItems="center">
                      <UploadIconBox>
                        <Icon icon="upload" color="primary" fontSize="large" />
                      </UploadIconBox>
                      <Stack spacing={1}>
                        <Typography variant="h6" fontWeight={600}>
                          {uploadButtonText}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {uploadDescription}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {instructionText}
                        </Typography>
                        <Button data-testid="browse-button" variant="contained">
                          Browse Files
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </StyledUploadCard>
            </FileUploadsDropWrapper>
          </Stack>
          {!compact && (
            <Stack>
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                {technicalSpecs}
              </Typography>
            </Stack>
          )}
        </Stack>
      </StyledCardContent>
    </StyledCard>
  );
};
